import React from 'react';
import { useFormikContext } from 'formik';
import S3FileUploader from '.'; // Import your existing S3 uploader
import { Button, Box, Typography, Stack } from '@mui/material';
import { Filename } from '../Filename';

interface FileValue {
  uuid?: string; // For prepopulated file
  filename?: string;
  mimetype?: string;
  extension?: string;
  url?: string;
  size?: number;
  name?: string; // For newly uploaded file
  key?: string;
  type?: string;
}

interface FormikS3FileUploaderProps {
  name: string; // Field name in Formik
  uploadEndpoint: string;
  multiple?: boolean;
}

const FormikS3FileUploader: React.FC<FormikS3FileUploaderProps> = ({
  name,
  uploadEndpoint,
  multiple = false,
}) => {
  const { setFieldValue, values, errors, touched } = useFormikContext<{
    [key: string]: FileValue | FileValue[];
  }>();

  const fileValue = values[name];

  const handleUploadComplete = (uploadedFiles: FileValue[]) => {
    if (multiple) {
      setFieldValue(name, [
        ...(Array.isArray(fileValue) ? fileValue : []),
        ...uploadedFiles,
      ]);
    } else {
      setFieldValue(name, uploadedFiles[0] || null);
    }
  };

  const handleRemoveFile = () => {
    setFieldValue(name, multiple ? [] : null);
  };

  return (
    <Box>
      {/* If file is set, show file info */}
      {fileValue && (!Array.isArray(fileValue) || fileValue.length > 0) ? (
        <Box display="flex" alignItems="center" gap={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {Array.isArray(fileValue) ? (
              fileValue
                .map(file => (
                  <Filename
                    name={file.filename || file.name}
                    extension={file.extension}
                  />
                ))
                .join(', ')
            ) : (
              <Filename
                name={fileValue.filename || fileValue.name}
                extension={fileValue.extension}
              />
            )}
          </Stack>
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            onClick={handleRemoveFile}
          >
            Remove
          </Button>
        </Box>
      ) : (
        <S3FileUploader
          multiple={multiple}
          uploadEndpoint={uploadEndpoint}
          onUploadComplete={handleUploadComplete}
        />
      )}

      {errors[name] && touched[name] && (
        <Typography color="error" variant="body2">
          {typeof errors[name] === 'string'
            ? (errors[name] as string) // If it's a string, display directly
            : (errors[name] as Record<string, string>)?.filename ||
              'Invalid file format'}
        </Typography>
      )}
    </Box>
  );
};

export default FormikS3FileUploader;
